import {
  EmailReportFilterSource,
  type EmailReportFilterPropsUnion,
} from '@/pages/EmailReport/components'
import { type BusinessHoursHolidayHourPeriodProps } from '@/pages/OmniLocationDetails/components/AtomicField/AtomicFieldHolidayHour'
import { type BusinessHoursRegularHourPeriodProps } from '@/pages/OmniLocationDetails/components/AtomicField/AtomicFieldRegularHour'
import {
  ReviewNotificationsFilterSource,
  type ReviewNotificationsFilterPropsUnion,
} from '@/pages/ReviewNotification/constants'
import { type ReportDataType } from '@/services/types/notifications/mockNotifications'
import { PHOTO_GALLERY_URLS_UPLOAD_LIMIT } from '@/utils/constants/media/images'
import { PeriodTypes } from '@/utils/constants/storeLocationHours/periodTypes'
import {
  findDaysOverlaps,
  findSameDayOverlap,
  getLastDuplicateDateObjects,
} from '@/utils/functions/dates/overlapValidate'
import { hasDuplicates } from '@/utils/functions/helpers/hasDuplicates'
import { replaceStringVars } from '@/utils/functions/i18n/replaceStringVars'
import { type FormRule } from 'antd'
import { PhoneNumberUtil } from 'google-libphonenumber'
import { postcodeValidator } from 'postcode-validator'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

export type ValidationRulesKeys =
  | 'currentPassword'
  | 'email'
  | 'integer'
  | 'locationDetails.coreInformation.serviceAreas'
  | 'locationDetails.description'
  | 'locationDetails.socialMedia.businessEmail'
  | 'locationDetails.socialMedia.instagramHandle'
  | 'locationDetails.socialMedia.twitterHandle'
  | 'locationDetails.socialMedia.tikTokHandle'
  | 'locationDetails.socialMedia.logoDescription'
  | 'locationDetails.socialMedia.logoAltText'
  | 'locationDetails.yelp.businessOwner'
  | 'locationDetails.yelp.businessTextArea'
  | 'locationDetails.yelp.businessOwnerLastNameInitial'
  | 'locationDetails.photoGallery.description'
  | 'menusExperience.description'
  | 'menusExperience.imageUrl'
  | 'menusExperience.name'
  | 'menusExperience.internalName'
  | 'menusExperience.choiceName'
  | 'newPassword'
  | 'newPasswordConfirmation'
  | 'noValidation'
  | 'phone'
  | 'phoneRequired'
  | 'photoGalleryImagesUrls'
  | 'price'
  | 'required'
  | 'reviews.defaultReply'
  | 'reviews.googleReply'
  | 'reviews.yelpReply'
  | 'storeLocationCategory'
  | 'timeRange'
  | 'url'
  | 'zipCode'
  | 'imageUrl'
  | 'notifications.recipients.externalRecipients'
  | 'notifications.recipients.users'
  | 'emailReports.reportData.reports'
  | 'reviews.notification.filter'
  | 'reports.notification.filter'
  | 'notifications.recipients'
  | 'reviewResponseSettings.folders'
  | 'reviewResponseSettings.stores'
  | 'validationHour'
  | 'holidayHour'

export type ValidationRulesResponse = {
  [k in ValidationRulesKeys]: FormRule[]
}

type CustomCommonValidatorsType = {
  atLeastOneLowercase: FormRule
  noEmoji: FormRule
  noUrls: FormRule
  noHtmlTags: FormRule
  phone: FormRule
  imageUrl: FormRule
  noOnlyWhitespaces: FormRule
  noBreakLines: FormRule
}
type InvalidSplitHourProps = {
  identifier: string
  openingHourIndex: number
}

// https://www.regextester.com/93652
export const URL_REGEX =
  /^(http:\/\/www\.|https:\/\/www\.|http:\/\/|https:\/\/)?[a-z0-9]+([-.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/

export function useFormValidationRules(): ValidationRulesResponse {
  const { t } = useTranslation()
  const phoneUtil = PhoneNumberUtil.getInstance()

  const atLeastOneLowercaseRegex = /(.*[a-z].*)/g
  const atLeastOneUppercaseRegex = /(.*[A-Z].*)/g
  const atLeastOneNumberRegex = /(.*\d.*)/g
  const atLeastOneSpecialCharRegex = /(.*\W.*)/g
  const integerRegex = /^[0-9]+$/gm
  const letterRegex = /\p{Letter}/gu
  const instagramHandleRegex = /^[\w.]{3,30}$/
  const twitterHandleRegex = /^[\w.]{1,15}$/
  const tikTokHandleRegex = /^[\w.]{2,24}$/
  const emojiRegex = /\p{Emoji_Presentation}/gu

  const urlHttpsImagesRegex =
    /^(https:\/\/)[a-z0-9]+([-.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*?)(\.(jpg|jpeg|png))(\/.*)?/

  const multiLineUrlHttpsRegex =
    /(?:(?:https?|ftp|file):\/\/|www\.|ftp\.)(?:\([-A-Z0-9+&@#/%=~_|$?!:,.]*\)|[-A-Z0-9+&@#/%=~_|$?!:,.])*(?:\([-A-Z0-9+&@#/%=~_|$?!:,.]*\)|[A-Z0-9+&@#/%=~_|$])/im
  const htmlTagsRegex = /<[^<>]+>/g

  const customCommonValidators: CustomCommonValidatorsType = {
    atLeastOneLowercase: {
      validator: async (_, value) => {
        const hasRegexError = value.length !== 0 && !value.match(atLeastOneLowercaseRegex)
        if (hasRegexError) {
          throw new Error(t('validation.atLeastOneLowercase'))
        }
      },
    },
    noEmoji: {
      validator: async (_, value) => {
        const hasRegexError = emojiRegex.test(value)
        if (hasRegexError) throw new Error(t('validation.noEmoji'))
      },
    },
    noHtmlTags: {
      validator: async (_, value) => {
        const hasRegexError = htmlTagsRegex.test(value)
        if (hasRegexError) {
          throw new Error(t('validation.noHtmlTags'))
        }
      },
    },
    noUrls: {
      validator: async (_, value) => {
        const hasRegexError = multiLineUrlHttpsRegex.test(value)
        if (hasRegexError) {
          throw new Error(t('validation.url.none'))
        }
      },
    },
    phone: {
      validator: async (_, value?: string) => {
        if (value && !phoneUtil.isValidNumber(phoneUtil.parseAndKeepRawInput(value)))
          throw new Error(t('validation.phone'))
      },
    },
    imageUrl: {
      validator: async (_, value: string) => {
        if (value && !urlHttpsImagesRegex.test(value)) throw new Error(t('validation.imageUrl'))
      },
    },
    noOnlyWhitespaces: {
      validator: async (_, value?: string) => {
        const hasError = value?.trim().length === 0
        if (hasError) {
          throw new Error(t('validation.noOnlyWhitespaces'))
        }
      },
    },
    noBreakLines: {
      validator: async (_, value?: string) => {
        if (value && /[\r\n]+/g.test(value)) {
          throw new Error(t('validation.noBreakLines'))
        }
      },
    },
  }

  return {
    required: [{ required: true, message: t('validation.required') }],
    email: [
      { required: true, message: t('validation.email.required') },
      { type: 'email', message: t('validation.email.type') },
    ],
    photoGalleryImagesUrls: [
      ({ getFieldValue }) => ({
        validator: async (_, value: string) => {
          const valueWithoutSpace = value.replaceAll(' ', '')
          const urls = valueWithoutSpace.split(',')

          const hasReachedLimitQuantity = urls.length > PHOTO_GALLERY_URLS_UPLOAD_LIMIT

          if (hasReachedLimitQuantity) {
            const message = t('validation.photoGalleryImageUrl.quantity', {
              limitQuantity: PHOTO_GALLERY_URLS_UPLOAD_LIMIT.toString(),
            })
            throw new Error(message)
          }

          const existentUrls = getFieldValue('existentUrls')
          const urlAlreadyExists = existentUrls.some((url: string) => {
            const urlWithoutSpace = url.replaceAll(' ', '')
            return urls.includes(urlWithoutSpace)
          })
          const hasRepeatedUrls = hasDuplicates(urls)

          if (urlAlreadyExists || hasRepeatedUrls) {
            const message = t('validation.photoGalleryImageUrl.repeatedUrls')
            throw new Error(message)
          }

          const hasRegexError = urls.some((url) => {
            const match = urlHttpsImagesRegex.test(url)
            return !match
          })
          if (value && hasRegexError) {
            const message = t('validation.photoGalleryImageUrl.pattern')
            throw new Error(message)
          }
        },
      }),
    ],
    url: [
      {
        validator: async (_, url?: string) => {
          const schema = z
            .union([z.string().regex(URL_REGEX), z.literal('')])
            .refine((value) => {
              const hasComma = value.includes(',')
              return !hasComma
            })
            .optional()

          if (!schema.safeParse(url).success) throw new Error(t('validation.url.pattern'))
        },
      },
    ],
    currentPassword: [
      { required: true, message: t('validation.currentPassword.required') },
      {
        min: 8,
        message: t('validation.currentPassword.min'),
      },
    ],
    newPassword: [
      { required: true, message: t('validation.newPassword.required') },
      {
        min: 8,
        message: t('validation.newPassword.min'),
      },
      {
        pattern: atLeastOneLowercaseRegex,
        message: t('validation.newPassword.atLeastOneLowercase'),
      },
      {
        pattern: atLeastOneUppercaseRegex,
        message: t('validation.newPassword.atLeastOneUppercase'),
      },
      {
        pattern: atLeastOneNumberRegex,
        message: t('validation.newPassword.atLeastOneNumber'),
      },
      {
        pattern: atLeastOneSpecialCharRegex,
        message: t('validation.newPassword.atLeastOneSpecialChar'),
      },
    ],
    newPasswordConfirmation: [
      {
        required: true,
        message: t('validation.newPasswordConfirmation.required'),
      },
      ({ getFieldValue }) => ({
        validator: async (_, value) => {
          if (!value || getFieldValue('newPassword') === value) {
            return
          }
          throw new Error(t('validation.newPasswordConfirmation.match'))
        },
      }),
    ],
    phone: [customCommonValidators.phone],
    phoneRequired: [
      { required: true, message: t('validation.required') },
      customCommonValidators.phone,
    ],
    noValidation: [],
    storeLocationCategory: [
      {
        validator: async (_, values: string[]) => {
          if (!values?.length || values.filter((v) => !v).length) {
            throw new Error(t('validation.storeLocationCategory.required'))
          }
        },
      },
    ],
    timeRange: [
      {
        validator: async (_, values) => {
          if (values && values.length > 1 && !!values[0] && !!values[1]) {
            return
          }
          throw new Error(t('validation.required'))
        },
      },
    ],
    integer: [{ pattern: integerRegex, message: t('validation.integer') }],
    price: [
      {
        validator: async (_, value: string) => {
          if (!value) return

          const number = parseFloat(value)

          if (isNaN(number)) throw new Error(t('validation.price.format'))

          if (number < 0) {
            throw new Error(t('validation.price.min'))
          }

          if (number > 999999999.99) {
            throw new Error(t('validation.price.max'))
          }
        },
      },
    ],
    zipCode: [
      { required: true, message: t('validation.required') },
      ({ getFieldValue }) => ({
        validator: async (_, value) => {
          if (!value) return

          const country: string = getFieldValue('country')
          const isValid = postcodeValidator(value, country.toUpperCase())

          if (!isValid) {
            throw new Error(t('validation.zipCode'))
          }
        },
      }),
    ],
    imageUrl: [customCommonValidators.imageUrl],
    'menusExperience.name': [
      {
        required: true,
        message: t('validation.menusExperience.name.required'),
      },
      { max: 500, message: t('validation.menusExperience.name.max') },
      customCommonValidators.noOnlyWhitespaces,
    ],
    'menusExperience.internalName': [
      { max: 500, message: t('validation.menusExperience.internalName.max') },
    ],
    'menusExperience.description': [
      { max: 500, message: t('validation.menusExperience.description') },
    ],
    'menusExperience.imageUrl': [
      { max: 800, message: t('validation.menusExperience.imageUrl.max') },
      customCommonValidators.imageUrl,
    ],
    'menusExperience.choiceName': [
      {
        required: true,
        message: t('validation.required'),
      },
      {
        max: 140,
        message: t('validation.menusExperience.choiceName.max', { max: 140 }),
      },
    ],
    'reviews.defaultReply': [
      { required: true, message: t('validation.required') },
      { max: 4000, message: t('validation.reviews.defaultReply.max') },
    ],
    'reviews.googleReply': [
      { required: true, message: t('validation.required') },
      { max: 4000, message: t('validation.reviews.googleReply.max') },
    ],
    'reviews.yelpReply': [
      { required: true, message: t('validation.required') },
      { max: 5000, message: t('validation.reviews.yelpReply.max') },
      customCommonValidators.noEmoji,
    ],
    'locationDetails.coreInformation.serviceAreas': [
      {
        required: true,
        whitespace: true,
        message: t('validation.locationDetails.requiredArea'),
      },
      {
        max: 500,
        message: replaceStringVars({
          text: t('validation.locationDetails.max'),
          vars: ['500'],
        }),
      },
    ],
    'locationDetails.description': [
      {
        max: 750,
        message: replaceStringVars({
          text: t('validation.locationDetails.max'),
          vars: ['750'],
        }),
      },
      {
        min: 15,
        message: replaceStringVars({
          text: t('validation.locationDetails.min'),
          vars: ['15'],
        }),
      },
      customCommonValidators.atLeastOneLowercase,
      customCommonValidators.noEmoji,
      customCommonValidators.noHtmlTags,
      customCommonValidators.noUrls,
    ],
    'locationDetails.yelp.businessOwner': [
      {
        max: 59,
        message: replaceStringVars({
          text: t('validation.locationDetails.max'),
          vars: ['59'],
        }),
      },
      customCommonValidators.noEmoji,
    ],
    'locationDetails.yelp.businessOwnerLastNameInitial': [
      {
        max: 1,
        message: replaceStringVars({
          text: t('validation.locationDetails.max'),
          vars: ['1'],
        }),
      },
      {
        validator: async (_, value) => {
          const hasRegexError = value.length !== 0 && !value.match(letterRegex)
          if (hasRegexError) {
            throw new Error(t('validation.onlyLetters'))
          }
        },
      },
    ],
    'locationDetails.yelp.businessTextArea': [
      {
        max: 1000,
        message: replaceStringVars({
          text: t('validation.locationDetails.max'),
          vars: ['1000'],
        }),
      },
      {
        min: 15,
        message: replaceStringVars({
          text: t('validation.locationDetails.min'),
          vars: ['15'],
        }),
      },
      customCommonValidators.atLeastOneLowercase,
      customCommonValidators.noEmoji,
      customCommonValidators.noHtmlTags,
      customCommonValidators.noUrls,
    ],
    'locationDetails.socialMedia.instagramHandle': [
      {
        min: 3,
        message: replaceStringVars({
          text: t('validation.locationDetails.min'),
          vars: ['3'],
        }),
      },
      {
        max: 30,
        message: replaceStringVars({
          text: t('validation.locationDetails.max'),
          vars: ['30'],
        }),
      },
      {
        validator: async (_, value) => {
          const hasRegexError = value.length !== 0 && !instagramHandleRegex.test(value)
          if (hasRegexError) {
            throw new Error(t('validation.locationDetails.socialMedia.type'))
          }
        },
      },
    ],
    'locationDetails.socialMedia.twitterHandle': [
      {
        min: 1,
        message: replaceStringVars({
          text: t('validation.locationDetails.min'),
          vars: ['1'],
        }),
      },
      {
        max: 15,
        message: replaceStringVars({
          text: t('validation.locationDetails.max'),
          vars: ['15'],
        }),
      },
      {
        validator: async (_, value) => {
          const hasRegexError = value.length !== 0 && !twitterHandleRegex.test(value)
          if (hasRegexError) {
            throw new Error(t('validation.locationDetails.socialMedia.type'))
          }
        },
      },
    ],
    'locationDetails.socialMedia.tikTokHandle': [
      {
        min: 2,
        message: replaceStringVars({
          text: t('validation.locationDetails.min'),
          vars: ['2'],
        }),
      },
      {
        max: 24,
        message: replaceStringVars({
          text: t('validation.locationDetails.max'),
          vars: ['24'],
        }),
      },
      {
        validator: async (_, value) => {
          const hasRegexError = value.length !== 0 && !tikTokHandleRegex.test(value)
          if (hasRegexError) {
            throw new Error(t('validation.locationDetails.socialMedia.type'))
          }
        },
      },
    ],
    'locationDetails.socialMedia.businessEmail': [
      { type: 'email', message: t('validation.email.type') },
    ],
    'locationDetails.socialMedia.logoDescription': [
      {
        max: 250,
        message: replaceStringVars({
          text: t('validation.locationDetails.max'),
          vars: ['250'],
        }),
      },
      customCommonValidators.noEmoji,
      customCommonValidators.noHtmlTags,
      customCommonValidators.noUrls,
    ],
    'locationDetails.socialMedia.logoAltText': [
      {
        max: 250,
        message: replaceStringVars({
          text: t('validation.locationDetails.max'),
          vars: ['250'],
        }),
      },
      customCommonValidators.noEmoji,
      customCommonValidators.noHtmlTags,
      customCommonValidators.noUrls,
    ],
    'locationDetails.photoGallery.description': [customCommonValidators.noBreakLines],
    'reviews.notification.filter': [
      {
        validator: async (_, filter: ReviewNotificationsFilterPropsUnion) => {
          switch (filter.source) {
            case ReviewNotificationsFilterSource.location:
              if (filter.stores.length === 0) {
                throw new Error(t('validation.required'))
              }
              break
            case ReviewNotificationsFilterSource.folder:
              if (filter.folders.length === 0) {
                throw new Error(t('validation.required'))
              }
              break
            case ReviewNotificationsFilterSource.publisher:
              if (filter.publishers.length === 0) {
                throw new Error(t('validation.required'))
              }
              break
            case ReviewNotificationsFilterSource.content:
              if (filter.contents.length === 0) {
                throw new Error(t('validation.required'))
              }
              break
            case ReviewNotificationsFilterSource.label:
              if (filter.labels.length === 0) {
                return Promise.reject(new Error(t('validation.required')))
              }
              break
          }
        },
      },
    ],
    'reports.notification.filter': [
      {
        validator: async (_, filter: EmailReportFilterPropsUnion) => {
          switch (filter.source) {
            case EmailReportFilterSource.location:
              if (filter.stores.length === 0) {
                throw new Error(t('validation.required'))
              }
              break
            case EmailReportFilterSource.folder:
              if (filter.folders.length === 0) {
                throw new Error(t('validation.required'))
              }
              break
            case EmailReportFilterSource.publisher:
              if (filter.publishers.length === 0) {
                throw new Error(t('validation.required'))
              }
              break
          }
        },
      },
    ],
    'notifications.recipients.externalRecipients': [
      {
        validator: async (_, values: string[]) => {
          const I18N_PREFIX = 'validation.notifications.recipients.externalRecipients'

          if (values.length === 0) {
            throw new Error(t(`${I18N_PREFIX}.required`))
          }

          const hasInvalidEmails = values.filter(
            (value) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value),
          )
          if (hasInvalidEmails.length !== 0) {
            throw new Error(t(`${I18N_PREFIX}.invalidEmail`))
          }
        },
      },
    ],
    'notifications.recipients.users': [
      {
        validator: async (_, values: string[]) => {
          const I18N_PREFIX = 'validation.notifications.recipients.users'

          if (values.length === 0) {
            throw new Error(t(`${I18N_PREFIX}.required`))
          }
        },
      },
    ],
    'notifications.recipients': [
      ({ getFieldsValue }) => ({
        validator: async () => {
          const formValues = getFieldsValue()

          if (!Object.values(formValues.checkboxes).some(Boolean)) {
            throw new Error(t(`validation.notifications.recipients.noRecipientsSelected`))
          }
        },
      }),
    ],
    'emailReports.reportData.reports': [
      {
        validator: async (_, values: ReportDataType[]) => {
          if (values.length === 0) {
            throw new Error(t('validation.emailReports.reportData.reports'))
          }
        },
      },
    ],
    'reviewResponseSettings.folders': [
      {
        validator: async (_, values: ReportDataType[]) => {
          if (values.length === 0) {
            throw new Error(t('validation.reviewResponseSetting.folders'))
          }
        },
      },
    ],
    'reviewResponseSettings.stores': [
      {
        validator: async (_, values: ReportDataType[]) => {
          if (values.length === 0) {
            throw new Error(t('validation.reviewResponseSetting.stores'))
          }
        },
      },
    ],
    validationHour: [
      ({ getFieldsValue, setFields, resetFields }) => ({
        validator: async (_, value: BusinessHoursRegularHourPeriodProps) => {
          const days = Object.values(getFieldsValue()) as BusinessHoursRegularHourPeriodProps[]

          resetFields()

          const hasOverlappingPeriods = findDaysOverlaps(days)

          if (hasOverlappingPeriods.length > 0) {
            setFields(
              hasOverlappingPeriods.map((day) => ({
                name: day,
                errors: [t('validation.overlapDays')],
              })),
            )
          }

          days.forEach((day) => {
            const { periodType, dayOfTheWeek, openingHours } = day

            if (periodType === PeriodTypes.SPLIT) {
              const validOpeningHours = openingHours.filter((h) => !!h && !!h[0] && !!h[1])

              const invalidSplitHours = openingHours
                .map((hour, index) => {
                  const otherHours = validOpeningHours.filter((_, i) => index !== i)
                  if (findSameDayOverlap(hour, otherHours)) {
                    return {
                      identifier: dayOfTheWeek,
                      openingHourIndex: index,
                    }
                  }
                  return null
                })
                .filter(Boolean) as InvalidSplitHourProps[]

              if (invalidSplitHours.length > 0) {
                setFields(
                  invalidSplitHours.map((hour) => ({
                    name: [hour.identifier],
                    errors: [t('validation.splitHours')],
                  })),
                )
              }
              if (!openingHours?.every((h) => !!h && !!h[0] && !!h[1])) {
                setFields([
                  {
                    name: [dayOfTheWeek],
                    errors: [''],
                    validating: true,
                  },
                ])
              }
            } else if (periodType === PeriodTypes.OPEN) {
              const isValid = openingHours?.every((h) => !!h && !!h[0] && !!h[1])
              if (!isValid) {
                setFields([
                  {
                    name: [dayOfTheWeek],
                    errors: [''],
                    validating: true,
                  },
                ])
              }
            }
          })

          if (!value) throw new Error('value is required')
        },
      }),
    ],
    holidayHour: [
      ({ getFieldsValue, setFields, resetFields }) => ({
        validator: async () => {
          const days = Object.values(getFieldsValue()) as BusinessHoursHolidayHourPeriodProps[]

          resetFields()

          const hasDuplicatesDates = getLastDuplicateDateObjects(days)

          hasDuplicatesDates.forEach((date) => {
            setFields([
              {
                name: date.id,
                errors: [t('validation.hasDuplicatesDates')],
              },
            ])
          })

          days.forEach((day) => {
            const { periodType, id, openingHours } = day

            if (periodType === PeriodTypes.SPLIT) {
              const validOpeningHours = openingHours.filter((h) => !!h && !!h[0] && !!h[1])

              const invalidSplitHours = openingHours
                .map((hour, index) => {
                  const otherHours = validOpeningHours.filter((_, i) => index !== i)
                  if (findSameDayOverlap(hour, otherHours)) {
                    return {
                      identifier: id,
                      openingHourIndex: index,
                    }
                  }
                  return null
                })
                .filter(Boolean) as InvalidSplitHourProps[]

              if (invalidSplitHours.length > 0) {
                setFields(
                  invalidSplitHours.map((hour) => ({
                    name: [hour.identifier],
                    errors: [t('validation.splitHours')],
                  })),
                )
              }
              if (!openingHours?.every((h) => !!h && !!h[0] && !!h[1])) {
                setFields([
                  {
                    name: [id],
                    errors: [''],
                    validating: true,
                  },
                ])
              }
            } else if (periodType === PeriodTypes.OPEN) {
              const isValid = openingHours?.every((h) => !!h && !!h[0] && !!h[1])
              if (!isValid) {
                setFields([
                  {
                    name: [id],
                    errors: [''],
                    validating: true,
                  },
                ])
              }
            }
          })
        },
      }),
    ],
  }
}
